import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import articlesData from '../data/articles.json';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  content: {
    sections: {
      heading: string;
      body: string;
    }[];
  };
  image: string;
  category: string;
  tags: string[];
  authorName: string;
  authorImage: string;
  authorBio: string;
  publishDate: string;
  readingTime: string;
  sources: string[];
}

const ArticleContainer = styled.article`
  min-height: 100vh;
  background: var(--off-white);
`;

const HeroSection = styled.section`
  position: relative;
  height: 60vh;
  min-height: 500px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 50vh;
    min-height: 400px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 10, 10, 0.8) 0%,
    rgba(10, 10, 10, 0.3) 50%,
    transparent 100%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: var(--space-12);
  
  @media (max-width: 768px) {
    padding: var(--space-8) var(--space-6);
  }
`;

const CategoryBadge = styled.span`
  background: var(--racing-gradient);
  color: var(--white);
  padding: var(--space-2) var(--space-6);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-bold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--space-6);
  align-self: flex-start;
  box-shadow: var(--shadow-racing);
`;

const HeroTitle = styled.h1`
  color: var(--white);
  font-size: var(--text-5xl);
  font-weight: var(--font-black);
  line-height: var(--leading-tight);
  margin-bottom: var(--space-4);
  
  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  color: var(--chrome-silver);
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4);
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--space-16);
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: var(--space-12);
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-6);
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  margin-bottom: var(--space-8);
`;

const AuthorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: var(--radius-full);
  object-fit: cover;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h3`
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-1);
`;

const AuthorBio = styled.p`
  font-size: var(--text-sm);
  color: var(--medium-gray);
  line-height: var(--leading-relaxed);
  margin: 0;
`;

const ContentSection = styled.section`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: var(--space-8);
  box-shadow: var(--shadow-md);
  margin-bottom: var(--space-6);
`;

const SectionHeading = styled.h2`
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-6);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--electric-gradient);
    border-radius: var(--radius-full);
  }
`;

const SectionBody = styled.div`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--dark-gray);
  
  p {
    margin-bottom: var(--space-6);
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  
  @media (max-width: 1024px) {
    order: -1;
  }
`;

const SidebarCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  box-shadow: var(--shadow-md);
`;

const SidebarTitle = styled.h3`
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-4);
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const Tag = styled.span`
  background: var(--light-gray);
  color: var(--carbon-black);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  transition: all var(--transition-fast);
  
  &:hover {
    background: var(--racing-red);
    color: var(--white);
    transform: translateY(-1px);
  }
`;

const SourcesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SourceItem = styled.li`
  padding: var(--space-2) 0;
  font-size: var(--text-sm);
  color: var(--medium-gray);
  border-bottom: 1px solid var(--light-gray);
  
  &:last-child {
    border-bottom: none;
  }
  
  &::before {
    content: '•';
    color: var(--racing-red);
    font-weight: bold;
    margin-right: var(--space-2);
  }
`;

const NavigationSection = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-8);
  background: var(--carbon-gradient);
  border-radius: var(--radius-xl);
  margin-top: var(--space-12);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-4);
    text-align: center;
  }
`;

const NavButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  background: var(--racing-gradient);
  color: var(--white);
  text-decoration: none;
  border-radius: var(--radius-full);
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  transition: all var(--transition-fast);
  
  &:hover {
    background: var(--racing-red-dark);
    transform: translateY(-2px);
    box-shadow: var(--shadow-racing);
    text-decoration: none;
  }
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--chrome-silver);
  text-decoration: none;
  font-size: var(--text-base);
  font-weight: var(--font-medium);
  transition: color var(--transition-fast);
  
  &:hover {
    color: var(--racing-red);
    text-decoration: none;
  }
`;

const ArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!id) {
    return <Navigate to="/" replace />;
  }

  const article = articlesData.find((a: Article) => a.id === parseInt(id));

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const currentIndex = articlesData.findIndex((a: Article) => a.id === parseInt(id));
  const prevArticle = currentIndex > 0 ? articlesData[currentIndex - 1] : null;
  const nextArticle = currentIndex < articlesData.length - 1 ? articlesData[currentIndex + 1] : null;

  return (
    <ArticleContainer>
      <HeroSection>
        <HeroImage src={article.image} alt={article.title} />
        <HeroOverlay>
          <CategoryBadge>{article.category}</CategoryBadge>
          <HeroTitle>{article.title}</HeroTitle>
          <HeroMeta>
            <span>By {article.authorName}</span>
            <span>•</span>
            <span>{formatDate(article.publishDate)}</span>
            <span>•</span>
            <span>{article.readingTime}</span>
          </HeroMeta>
        </HeroOverlay>
      </HeroSection>

      <ContentContainer>
        <ContentGrid>
          <MainContent>
            <AuthorSection>
              <AuthorImage src={article.authorImage} alt={article.authorName} />
              <AuthorInfo>
                <AuthorName>{article.authorName}</AuthorName>
                <AuthorBio>{article.authorBio}</AuthorBio>
              </AuthorInfo>
            </AuthorSection>

            {article.content.sections.map((section, index) => (
              <ContentSection key={index}>
                <SectionHeading>{section.heading}</SectionHeading>
                <SectionBody>
                  <p>{section.body}</p>
                </SectionBody>
              </ContentSection>
            ))}
          </MainContent>

          <Sidebar>
            <SidebarCard>
              <SidebarTitle>Tags</SidebarTitle>
              <TagsList>
                {article.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </TagsList>
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>Sources</SidebarTitle>
              <SourcesList>
                {article.sources.map((source, index) => (
                  <SourceItem key={index}>{source}</SourceItem>
                ))}
              </SourcesList>
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>Article Details</SidebarTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div>
                  <strong>Published:</strong> {formatDate(article.publishDate)}
                </div>
                <div>
                  <strong>Reading Time:</strong> {article.readingTime}
                </div>
                <div>
                  <strong>Category:</strong> {article.category}
                </div>
              </div>
            </SidebarCard>
          </Sidebar>
        </ContentGrid>

        <NavigationSection>
          <BackButton to="/">
            ← Back to Articles
          </BackButton>
          
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            {prevArticle && (
              <NavButton to={`/article/${prevArticle.id}`}>
                ← Previous
              </NavButton>
            )}
            {nextArticle && (
              <NavButton to={`/article/${nextArticle.id}`}>
                Next →
              </NavButton>
            )}
          </div>
        </NavigationSection>
      </ContentContainer>
    </ArticleContainer>
  );
};

export default ArticlePage;
